
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, ShoppingCart, Wrench, Headphones, Star, CheckCircle, ArrowRight } from 'lucide-react';

const ServicesSection = () => {
  const services = [
  {
    icon: <Users className="w-8 h-8" data-id="ahms86cri" data-path="src/components/ServicesSection.tsx" />,
    title: 'Asesoría Experta',
    description: 'Análisis detallado de tu hogar, evaluación de compatibilidad y diseño de soluciones personalizadas que se adapten a tu estilo de vida.',
    features: ['Consulta gratuita', 'Análisis de compatibilidad', 'Diseño personalizado', 'Planificación de proyecto'],
    price: 'Gratis',
    color: 'from-blue-500 to-indigo-500'
  },
  {
    icon: <ShoppingCart className="w-8 h-8" data-id="hdqjnlne6" data-path="src/components/ServicesSection.tsx" />,
    title: 'Venta de Equipos Premium',
    description: 'Las mejores marcas del mercado con precios competitivos. HomeSeer, Insteon, Fibaro, Lutron, Sonos y más.',
    features: ['Mejores marcas', 'Precios competitivos', 'Garantía extendida', 'Envío incluido'],
    price: 'Desde $2,500',
    color: 'from-green-500 to-emerald-500'
  },
  {
    icon: <Wrench className="w-8 h-8" data-id="rqdurapdf" data-path="src/components/ServicesSection.tsx" />,
    title: 'Instalación Profesional',
    description: 'Técnicos certificados con años de experiencia aseguran una instalación perfecta y configuración optimizada.',
    features: ['Técnicos certificados', 'Instalación completa', 'Configuración optimizada', 'Pruebas exhaustivas'],
    price: 'Desde $5,000',
    color: 'from-purple-500 to-pink-500'
  },
  {
    icon: <Headphones className="w-8 h-8" data-id="o4j2yz64o" data-path="src/components/ServicesSection.tsx" />,
    title: 'Soporte Post-Venta',
    description: 'Garantía de satisfacción y asistencia técnica continua. Estamos aquí para resolver cualquier duda o problema.',
    features: ['Soporte 24/7', 'Garantía extendida', 'Actualizaciones gratuitas', 'Mantenimiento preventivo'],
    price: 'Incluido',
    color: 'from-orange-500 to-red-500'
  }];


  const processSteps = [
  {
    number: '01',
    title: 'Consulta Inicial',
    description: 'Evaluamos tu hogar y necesidades específicas',
    icon: '🏠'
  },
  {
    number: '02',
    title: 'Propuesta Personalizada',
    description: 'Diseñamos la solución perfecta para ti',
    icon: '📋'
  },
  {
    number: '03',
    title: 'Instalación Profesional',
    description: 'Nuestros técnicos certificados instalan todo',
    icon: '🔧'
  },
  {
    number: '04',
    title: 'Configuración y Pruebas',
    description: 'Configuramos y probamos cada función',
    icon: '⚙️'
  },
  {
    number: '05',
    title: 'Entrega y Capacitación',
    description: 'Te enseñamos a usar tu nuevo hogar inteligente',
    icon: '🎓'
  }];


  return (
    <section id="servicios" className="py-20 bg-white" data-id="lt9ygb2ns" data-path="src/components/ServicesSection.tsx">
      <div className="container mx-auto px-4" data-id="njfku2g8y" data-path="src/components/ServicesSection.tsx">
        {/* Header */}
        <div className="text-center mb-16" data-id="u2u7ss7cs" data-path="src/components/ServicesSection.tsx">
          <Badge className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 text-sm font-medium mb-4" data-id="uyh8lnz8b" data-path="src/components/ServicesSection.tsx">
            🛠️ Servicios Profesionales
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6" data-id="cc5gaw7fj" data-path="src/components/ServicesSection.tsx">
            Servicios que
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent" data-id="3nnpd20wt" data-path="src/components/ServicesSection.tsx"> Transforman</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed" data-id="k7z39485y" data-path="src/components/ServicesSection.tsx">
            Desde la consulta inicial hasta el soporte continuo, te acompañamos en cada paso 
            para crear el hogar inteligente de tus sueños.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16" data-id="esvi46ac3" data-path="src/components/ServicesSection.tsx">
          {services.map((service, index) =>
          <Card key={index} className="group hover:shadow-2xl transition-all duration-300 border-0 overflow-hidden h-full" data-id="6s9ct4wgo" data-path="src/components/ServicesSection.tsx">
              <CardContent className="p-6 h-full flex flex-col" data-id="5wea5jwce" data-path="src/components/ServicesSection.tsx">
                <div className={`inline-flex p-3 rounded-lg bg-gradient-to-r ${service.color} text-white mb-4 group-hover:scale-110 transition-transform duration-300 w-fit`} data-id="qs7yylamz" data-path="src/components/ServicesSection.tsx">
                  {service.icon}
                </div>
                
                <div className="flex-1" data-id="mqdbvl2pc" data-path="src/components/ServicesSection.tsx">
                  <div className="flex items-center justify-between mb-3" data-id="8lnpnwud9" data-path="src/components/ServicesSection.tsx">
                    <h3 className="text-xl font-bold text-gray-900" data-id="b5vv0g080" data-path="src/components/ServicesSection.tsx">{service.title}</h3>
                    <Badge variant="outline" className="text-green-600 border-green-600" data-id="n5dm51z52" data-path="src/components/ServicesSection.tsx">
                      {service.price}
                    </Badge>
                  </div>
                  
                  <p className="text-gray-600 mb-4 leading-relaxed" data-id="18g2jnr11" data-path="src/components/ServicesSection.tsx">{service.description}</p>
                  
                  <div className="space-y-2" data-id="do7g8efdk" data-path="src/components/ServicesSection.tsx">
                    {service.features.map((feature, featureIndex) =>
                  <div key={featureIndex} className="flex items-center space-x-2" data-id="c6kzc6nhf" data-path="src/components/ServicesSection.tsx">
                        <CheckCircle className="w-4 h-4 text-green-500" data-id="9uvu16wzw" data-path="src/components/ServicesSection.tsx" />
                        <span className="text-sm text-gray-700" data-id="9k6wzanr3" data-path="src/components/ServicesSection.tsx">{feature}</span>
                      </div>
                  )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Process Section */}
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-2xl p-8 lg:p-12" data-id="so7frlktn" data-path="src/components/ServicesSection.tsx">
          <div className="text-center mb-12" data-id="echxrl1ap" data-path="src/components/ServicesSection.tsx">
            <h3 className="text-3xl font-bold text-gray-900 mb-4" data-id="oatzkhmiu" data-path="src/components/ServicesSection.tsx">
              Nuestro Proceso de Trabajo
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="2r370ds9e" data-path="src/components/ServicesSection.tsx">
              Un proceso estructurado y probado que garantiza resultados excepcionales en cada proyecto.
            </p>
          </div>

          <div className="grid md:grid-cols-5 gap-6 mb-12" data-id="ebhton9cb" data-path="src/components/ServicesSection.tsx">
            {processSteps.map((step, index) =>
            <div key={index} className="text-center group" data-id="hwxyqm63u" data-path="src/components/ServicesSection.tsx">
                <div className="relative" data-id="c34maujse" data-path="src/components/ServicesSection.tsx">
                  <div className="bg-white rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow duration-300" data-id="cu74p3aq6" data-path="src/components/ServicesSection.tsx">
                    <span className="text-2xl" data-id="mdng5nrac" data-path="src/components/ServicesSection.tsx">{step.icon}</span>
                  </div>
                  <div className="absolute -top-2 -right-2 bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold" data-id="9l9pzor5m" data-path="src/components/ServicesSection.tsx">
                    {step.number}
                  </div>
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2" data-id="3pb1lxo9h" data-path="src/components/ServicesSection.tsx">{step.title}</h4>
                <p className="text-sm text-gray-600" data-id="pn9dxri37" data-path="src/components/ServicesSection.tsx">{step.description}</p>
              </div>
            )}
          </div>

          <div className="text-center" data-id="b0oymupj3" data-path="src/components/ServicesSection.tsx">
            <div className="bg-white rounded-xl p-6 shadow-lg inline-block" data-id="k8htcsgyj" data-path="src/components/ServicesSection.tsx">
              <div className="flex items-center space-x-4 mb-4" data-id="0k3bx6e74" data-path="src/components/ServicesSection.tsx">
                <Star className="w-6 h-6 text-yellow-400 fill-current" data-id="fp5qor3sp" data-path="src/components/ServicesSection.tsx" />
                <span className="text-lg font-semibold text-gray-900" data-id="d015z24ap" data-path="src/components/ServicesSection.tsx">Garantía de Satisfacción</span>
                <Star className="w-6 h-6 text-yellow-400 fill-current" data-id="zppawqmph" data-path="src/components/ServicesSection.tsx" />
              </div>
              <p className="text-gray-600 mb-4" data-id="c3oob28yy" data-path="src/components/ServicesSection.tsx">
                Si no quedas 100% satisfecho con nuestro trabajo, lo corregimos sin costo adicional.
              </p>
              <Button
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white" data-id="ce6mc8uqc" data-path="src/components/ServicesSection.tsx">

                Inicia tu Proyecto Hoy
                <ArrowRight className="w-5 h-5 ml-2" data-id="cjrf7yelu" data-path="src/components/ServicesSection.tsx" />
              </Button>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 lg:p-12 text-white text-center" data-id="d8gvxxmvh" data-path="src/components/ServicesSection.tsx">
          <h3 className="text-3xl font-bold mb-4" data-id="fjzs1hu2g" data-path="src/components/ServicesSection.tsx">
            ¿Listo para Transformar tu Hogar?
          </h3>
          <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto" data-id="y62tzqsjk" data-path="src/components/ServicesSection.tsx">
            Contáctanos hoy mismo y descubre cómo podemos hacer realidad el hogar inteligente que siempre has soñado.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="oea58x7p3" data-path="src/components/ServicesSection.tsx">
            <Button
              size="lg"
              variant="secondary"
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 font-medium" data-id="4ruy3zrf9" data-path="src/components/ServicesSection.tsx">

              Consulta Gratuita
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 font-medium bg-transparent hover:bg-white" data-id="hcmt8l1su" data-path="src/components/ServicesSection.tsx">

              Ver Proyectos
            </Button>
          </div>
        </div>
      </div>
    </section>);

};

export default ServicesSection;